import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, HelpCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useMaintenanceContext } from "@/contexts/MaintenanceContext";
import { AvisoDeManutencaoNoLogin } from "@/modules/maintenance/components/AvisoDeManutencaoNoLogin";
import { avisoDeManutencaoNoLogin } from "@/modules/maintenance/utils/avisoDeManutencaoNoLogin";

/**
 * Login Page — "The Ethereal Professional" Design System
 *
 * Design reference: docs/ALTERACOES_CORRECOES/REDESIGN_TELA_INICIAL/
 *
 * Review fixes applied:
 * - #1  Dark mode: uses ep-* Tailwind tokens with dark: variants
 * - #2  Material Symbols replaced with lucide-react (project standard)
 * - #3  All colors use ep-* tokens (no hardcoded hex)
 * - #4  autoComplete attributes on inputs
 * - #5  Logo uses /logo.png if exists, gradient circle fallback
 * - #6  Watermark implemented (AC1)
 * - #7  Error div has role="alert" aria-live="assertive"
 * - #8  RecuperarSenha visual consistency (separate task)
 * - #9  Font fallback: system-ui in fontFamily stack
 * - #10 Removed "use client" (Vite, not Next.js)
 * - #11 fadeIn moved to Tailwind config
 * - #12 CURRENT_YEAR extracted to module constant
 * - #13 Footer links replaced with spans (no dead href="#")
 */

const CURRENT_YEAR = new Date().getFullYear();

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Manutenção (28/09/2026, contrato Front — docs/superpowers/plans/
  // 2026-09-28-manutencao-no-login.md): separado do `error` vermelho de
  // sempre porque tem um tom diferente (aviso, não erro) — guarda a
  // mensagem mais recente vinda de uma TENTATIVA de login (mais precisa que
  // o polling do MaintenanceProvider, que pode estar alguns segundos
  // atrasado).
  const [avisoDeManutencaoNoSubmit, setAvisoDeManutencaoNoSubmit] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const manutencao = useMaintenanceContext();

  const from = (location.state as { from?: { pathname: string; search: string } })?.from;
  const returnTo = from ? `${from.pathname}${from.search || ""}` : "/";

  // Aviso GRANDE (decisão do Augusto, 28/09/2026): desde a contagem
  // regressiva da carência até o fim da trava. O 503 do SUBMIT veio de
  // verdade do backend — vale como "travada" mesmo antes de o polling saber,
  // e a mensagem dele é a mais fresca.
  const avisoDoEstado = avisoDeManutencaoNoLogin(manutencao);
  const aviso = avisoDeManutencaoNoSubmit !== null ? { tipo: "travada" as const } : avisoDoEstado;

  // Achado BAIXO (revisão de segurança, 28/09/2026, item 11): sem isto, o
  // aviso de um submit barrado por manutenção ficava preso na tela mesmo
  // depois da manutenção acabar de verdade (o contexto atualiza via
  // polling/WS, mas nada limpava o estado local do submit) — o admin via um
  // aviso de manutenção fantasma.
  useEffect(() => {
    if (!manutencao.isActive && avisoDeManutencaoNoSubmit) {
      setAvisoDeManutencaoNoSubmit(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só reage ao
    // fim da manutenção; incluir avisoDeManutencaoNoSubmit recriaria o efeito
    // a cada submit sem necessidade.
  }, [manutencao.isActive]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await login(email, password);
      setAvisoDeManutencaoNoSubmit(null);
      navigate(returnTo, { replace: true });
    } catch (err) {
      if (err instanceof Error && (err as Error & { manutencao?: boolean }).manutencao) {
        setAvisoDeManutencaoNoSubmit(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Erro ao fazer login");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-ep-surface dark:bg-gray-950 font-body text-ep-on-surface dark:text-gray-100 h-screen overflow-y-auto flex flex-col items-center selection:bg-ep-primary-container relative">
      {/* ── Abstract Background Blobs ── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[10%] -left-[5%] w-[40vw] h-[40vw] rounded-full blur-[120px] bg-ep-primary-fixed dark:bg-ep-primary/30 opacity-20" />
        <div className="absolute bottom-[5%] right-[2%] w-[35vw] h-[35vw] rounded-full blur-[100px] bg-ep-secondary-fixed dark:bg-ep-secondary-fixed/20 opacity-15" />
        {/* Watermark (AC1) — faint rotated logo silhouette */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url(/logo.png)",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "contain",
            filter: "grayscale(1) brightness(1.5) opacity(0.04)",
            transform: "scale(2) rotate(-5deg)",
          }}
        />
      </div>

      {/* ── Asymmetric Gradient Overlay ── */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background:
            "radial-gradient(circle at 10% 20%, rgba(178,235,255,0.15) 0%, transparent 40%), " +
            "radial-gradient(circle at 90% 80%, rgba(122,229,232,0.12) 0%, transparent 40%)",
        }}
      />

      {/* ── Top Header ── */}
      <header className="w-full top-0 left-0 bg-transparent flex justify-between items-center px-4 sm:px-8 py-6 max-w-screen-2xl mx-auto z-20">
        <button
          type="button"
          aria-label="Ajuda"
          className="text-ep-primary dark:text-ep-primary-container hover:opacity-70 transition-opacity"
          onClick={() => {
            /* TODO: help modal */
          }}
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </header>

      {/* ── Main Content ── */}
      <main className="flex-grow w-full flex items-center justify-center relative px-4 sm:px-6 z-10">
        <div className="w-full max-w-[480px]">
          {/* ── Glassmorphism Login Card ── */}
          <div className="bg-white/85 dark:bg-gray-900/85 backdrop-blur-[20px] rounded-xl p-6 sm:p-8 md:p-10 border border-ep-outline-variant/10 dark:border-gray-700/20 shadow-[0_12px_40px_rgba(42,52,57,0.06)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.3)]">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="/logo.png"
                alt="SE Promotora"
                className="w-20 h-20 sm:w-24 sm:h-24 object-contain"
                onError={(e) => {
                  // Fallback: hide img, show gradient circle
                  (e.target as HTMLImageElement).style.display = "none";
                  const fallback = (e.target as HTMLImageElement).nextElementSibling;
                  if (fallback) (fallback as HTMLElement).style.display = "flex";
                }}
              />
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-ep-primary-container to-ep-primary items-center justify-center hidden">
                <span className="text-white font-headline font-extrabold text-2xl sm:text-3xl">
                  SE
                </span>
              </div>
            </div>

            {/* Title */}
            <div className="mb-6 text-center">
              <h1 className="font-headline text-3xl sm:text-4xl font-extrabold tracking-tight text-ep-primary dark:text-ep-primary-container mb-2">
                SeWeb
              </h1>
              <p className="text-ep-on-surface-variant dark:text-gray-400 font-medium tracking-wide leading-relaxed text-sm sm:text-base">
                Bem-vindo
              </p>
            </div>

            {/* Aviso de manutenção: o admin precisa conseguir entrar mesmo
                assim, então NADA aqui desabilita o formulário. */}
            {aviso && (
              <AvisoDeManutencaoNoLogin
                tipo={aviso.tipo}
                publico="interno"
                mensagem={avisoDeManutencaoNoSubmit ?? manutencao.message}
                previsaoDeVolta={manutencao.estimatedEnd}
                fimDaContagem={aviso.tipo === "contagem" ? aviso.fimDaContagem : null}
              />
            )}

            {/* Error Message (#7: role="alert" for screen readers) */}
            {error && (
              <div
                role="alert"
                aria-live="assertive"
                className="mb-6 text-ep-error dark:text-red-400 text-sm font-medium animate-ep-fade-in"
              >
                {error}
              </div>
            )}

            {/* Form */}
            <form className="space-y-8 sm:space-y-10" onSubmit={handleSubmit}>
              {/* Email Field — Floating Label */}
              <div className="relative group">
                <input
                  id="login-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder=" "
                  className="peer block w-full bg-transparent border-0 border-b border-ep-outline-variant/20 dark:border-gray-600/30 px-0 py-3 text-ep-on-surface dark:text-gray-100 font-body focus:ring-0 focus:border-ep-primary dark:focus:border-ep-primary-container transition-all duration-300 placeholder-transparent outline-none"
                />
                <label
                  htmlFor="login-email"
                  className="absolute left-0 top-3 text-ep-on-surface-variant/70 dark:text-gray-500 font-label tracking-wide text-sm transition-all duration-300 pointer-events-none peer-focus:-top-4 peer-focus:text-ep-primary dark:peer-focus:text-ep-primary-container peer-focus:text-xs peer-[:not(:placeholder-shown)]:-top-4 peer-[:not(:placeholder-shown)]:text-xs"
                >
                  E-mail
                </label>
              </div>

              {/* Password Field — Floating Label */}
              <div className="relative group">
                <input
                  id="login-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder=" "
                  className="peer block w-full bg-transparent border-0 border-b border-ep-outline-variant/20 dark:border-gray-600/30 px-0 py-3 text-ep-on-surface dark:text-gray-100 font-body focus:ring-0 focus:border-ep-primary dark:focus:border-ep-primary-container transition-all duration-300 placeholder-transparent outline-none"
                />
                <label
                  htmlFor="login-password"
                  className="absolute left-0 top-3 text-ep-on-surface-variant/70 dark:text-gray-500 font-label tracking-wide text-sm transition-all duration-300 pointer-events-none peer-focus:-top-4 peer-focus:text-ep-primary dark:peer-focus:text-ep-primary-container peer-focus:text-xs peer-[:not(:placeholder-shown)]:-top-4 peer-[:not(:placeholder-shown)]:text-xs"
                >
                  Senha
                </label>
                <div className="absolute right-0 top-3 flex items-center">
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-ep-outline-variant dark:text-gray-500 hover:text-ep-primary dark:hover:text-ep-primary-container transition-colors focus:outline-none"
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link — desativado de verdade na manutenção
                  (a tela /recuperar-senha também fecha e volta para cá). */}
              {aviso ? (
                <div className="flex flex-col items-end gap-1 -mt-4">
                  <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    aria-describedby="motivo-esqueci-senha"
                    data-testid="esqueci-senha"
                    className="text-xs font-label uppercase tracking-widest text-ep-on-surface-variant/40 dark:text-gray-600 cursor-not-allowed select-none"
                  >
                    Esqueci minha senha
                  </button>
                  <p
                    id="motivo-esqueci-senha"
                    className="text-[11px] font-label text-amber-800/80 dark:text-amber-200/70"
                  >
                    Indisponível durante a manutenção
                  </p>
                </div>
              ) : (
                <div className="flex justify-end -mt-4">
                  <button
                    type="button"
                    data-testid="esqueci-senha"
                    onClick={() => navigate("/recuperar-senha")}
                    className="text-xs font-label uppercase tracking-widest text-ep-primary/70 dark:text-ep-primary-container/70 hover:text-ep-primary dark:hover:text-ep-primary-container transition-colors duration-300"
                  >
                    Esqueci minha senha
                  </button>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-2 sm:pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-ep-primary dark:bg-ep-primary/90 text-ep-on-primary font-headline font-bold py-3.5 sm:py-4 rounded-xl shadow-lg shadow-ep-primary/10 hover:opacity-90 active:scale-[0.98] transition-all duration-300 tracking-wide text-base sm:text-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    "Entrar"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="w-full bottom-0 bg-transparent flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 px-4 sm:px-8 py-8 sm:py-12 max-w-screen-2xl mx-auto z-20">
        <div className="flex flex-col items-center md:items-start gap-1">
          <p className="text-slate-400 dark:text-slate-600 font-label text-xs tracking-wider uppercase">
            &copy; {CURRENT_YEAR} SE Promotora.
          </p>
          <p className="flex items-center gap-2 text-slate-400 dark:text-slate-600 font-label text-xs tracking-wider uppercase">
            <img
              src="/ti_se.svg"
              alt="TI"
              className="h-9 w-auto inline-block align-middle bg-transparent"
            />
            <span>Setor de TI</span>
          </p>
        </div>
        <div className="flex gap-6 sm:gap-8">
          <span className="text-slate-400 dark:text-slate-600 font-label text-xs tracking-wider uppercase">
            Privacidade
          </span>
          <span className="text-slate-400 dark:text-slate-600 font-label text-xs tracking-wider uppercase">
            Termos
          </span>
          <span className="text-slate-400 dark:text-slate-600 font-label text-xs tracking-wider uppercase">
            Suporte
          </span>
        </div>
      </footer>
    </div>
  );
};

export default Login;
